import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, ShoppingBag, CreditCard, MessageSquare } from '../../lib/lucide-proxy';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import toast from 'react-hot-toast';

const Cart = ({ isOpen, onClose }) => {
  const { cart, updateQuantity, removeItem, getTotalItems, getTotalPrice, getDeliveryFee, getFinalTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    const config = cart.delivery_config || { type: 'platform' };

    if (config.type === 'whatsapp') {
      handleWhatsAppCheckout(config.whatsapp);
    } else if (config.type === 'external') {
      window.open(config.link, '_blank');
    } else {
      // Platform defaults
      if (!user) {
        navigate('/login', { state: { from: { pathname: '/checkout' } } });
        onClose();
        return;
      }
      navigate('/checkout');
      onClose();
    }
  };

  const handleWhatsAppCheckout = (phone) => {
    if (!phone) {
      toast.error('Cervejaria sem número configurado');
      return;
    }
    
    const itemsList = cart.items.map(item => 
      `- ${item.quantity}x ${item.name} (R$ ${(item.price * item.quantity).toFixed(2)})`
    ).join('\n');

    const total = getFinalTotal();
    
    const message = `Olá! Gostaria de fazer um pedido:\n\n${itemsList}\n\nTaxa de entrega: R$ ${getDeliveryFee().toFixed(2)}\n*Total: R$ ${total.toFixed(2)}*`;
    
    const cleanPhone = phone.replace(/\D/g, '');
    const url = `https://wa.me/55${cleanPhone}?text=${encodeURIComponent(message)}`;
    
    window.open(url, '_blank');
    clearCart(); // Optional: clear cart after sending
    onClose();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <ShoppingBag className="w-6 h-6 text-primary-500" />
                <h2 className="text-lg font-semibold text-dark-900">
                  Carrinho ({getTotalItems()})
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-dark-500 hover:text-dark-700 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {cart.items.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-dark-900 mb-2">
                    Seu carrinho está vazio
                  </h3>
                  <p className="text-dark-600 mb-6">
                    Adicione cervejas geladas para começar!
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => {
                      navigate('/cervejarias');
                      onClose();
                    }}
                  >
                    Explorar Cervejarias
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.items.map((item) => (
                    <div key={item.id} className="flex items-center space-x-4 bg-gray-50 rounded-xl p-4">
                      <img
                        src={item.image || 'https://images.unsplash.com/photo-1608270586620-248524c67de9?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80'}
                        alt={item.name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-dark-900 truncate">
                          {item.name}
                        </h4>
                        <p className="text-sm text-dark-600">
                          {item.brewery_name}
                        </p>
                        <p className="text-sm font-medium text-primary-600">
                          {formatPrice(item.price)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-dark-600 hover:bg-gray-50 transition-colors"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium text-dark-900">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-dark-600 hover:bg-gray-50 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.items.length > 0 && (
              <div className="border-t border-gray-200 p-6">
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-dark-600">
                    <span>Subtotal</span>
                    <span>{formatPrice(getTotalPrice())}</span>
                  </div>
                  <div className="flex justify-between text-dark-600">
                    <span>Taxa de entrega (Est.)</span>
                    <span>{formatPrice(getDeliveryFee())}</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold text-dark-900 pt-3 border-t border-gray-200">
                    <span>Total</span>
                    <span>{formatPrice(getFinalTotal())}</span>
                  </div>
                </div>

                {cart.brewery_id && (
                  <div className="bg-primary-50 rounded-xl p-3 mb-4">
                    <p className="text-sm text-primary-700">
                      📍 Pedido da <strong>{cart.brewery_name}</strong>
                    </p>
                  </div>
                )}

                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleCheckout}
                  className={`w-full flex items-center justify-center space-x-2 ${
                    cart.delivery_config?.type === 'whatsapp' 
                      ? 'bg-green-600 hover:bg-green-700 border-transparent' 
                      : ''
                  }`}
                >
                  {cart.delivery_config?.type === 'whatsapp' ? (
                    <>
                      <MessageSquare className="w-5 h-5" />
                      <span>Enviar Pedido no WhatsApp</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      <span>Finalizar Pedido</span>
                    </>
                  )}
                </Button>
                
                {cart.delivery_config?.type === 'platform' && !user && (
                  <p className="text-xs text-center text-dark-500 mt-2">
                    Você será redirecionado para fazer login
                  </p>
                )}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default Cart;